import { createContext, ReactNode, RefObject, useContext } from "react";
import { Product, ProductListConfig } from "../../types/types";
import {
  createProductListEventDetail,
  dispatchCustomEvent,
} from "../../utils/events/dispatchEvent";
import { useVariant } from "../VariantContext/VariantContext";
import { EventName } from "./productCardEvents";

type ProductCardEventContextType = {
  product: Product;
  config: ProductListConfig;
  index: number;
  eventsRef: RefObject<HTMLElement | null>;
  instanceId: string;
  variantId?: string;
  skuId?: string;
};

const ProductCardEventContext =
  createContext<ProductCardEventContextType | null>(null);

type Props = ProductCardEventContextType & {
  children: ReactNode;
};

export function ProductCardEventProvider(props: Props): React.JSX.Element {
  const { variant } = useVariant();
  const { children, ...nonChildrenProps } = props;

  return (
    <ProductCardEventContext.Provider
      value={{
        ...nonChildrenProps,
        variantId: variant?.id,
        skuId: variant?.aliases?.skuId,
      }}
    >
      {children}
    </ProductCardEventContext.Provider>
  );
}

export function useProductCardEvent() {
  const context = useContext(ProductCardEventContext);

  if (!context) {
    throw new Error(
      "useProductCardEvent must be used within a ProductCardEventProvider",
    );
  }

  const { product, index, variantId, skuId, instanceId } = context;

  return (type: EventName) => {
    dispatchCustomEvent(
      context.eventsRef,
      type,
      createProductListEventDetail({
        type,
        product,
        index,
        quantity: 1,
        instanceId,
        variantId,
        skuId,
      }),
    );
  };
}
